import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import SearchResultComponent from 'components/SearchResultComponent';
import UpsertResourcesForm from 'components/settings/resources/UpsertResourcesForm';
import { FormState } from "final-form";
import _ from 'lodash';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, GridColumn, GridRow, Loader, Modal } from 'semantic-ui-react';
import { getRapidTestCodes } from 'services/covidProductsServices';
import { getAllMandates } from 'services/mandateServices';
import { getAllResourceGroups } from 'services/resourceGroupServices';
import { deleteById, getAllLocations, getResourceById, searchResource } from 'services/resourcesServices';
import { getAllTreaters } from 'services/treaterServices';
import { debounce } from 'ts-debounce';
import {
  Location,
  MandateDto,
  ResourceDto,
  ResourceGroupDto,
  ResourceGroupSearchDto,
  ResourcesSearchDto,
  TreaterDto,
  UpsertResourceDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';

const initialSearchValues: Partial<ResourcesSearchDto> = {
  searchTerm: "",
  includesInactive: false
}

interface ResourcesProps extends FormState {
  values: Partial<ResourcesSearchDto>
}

interface Props extends MessageSourceProps {

}

interface State {
  foundResources: Array<ResourceDto>,
  resourcesLoaded: boolean,
  resource?: UpsertResourceDto,
  resourceId?: number,
  addOrEditResourceModal: boolean,
  errorMessage?: string,
  searchTerm: Partial<ResourcesSearchDto>,
  resourceGroups: ResourceGroupDto[],
  resourceGroupOptions: DropdownOption[],
  treaterOptions: DropdownOption[],
  locationOptions: DropdownOption[],
  mandateOptions: DropdownOption[],
  treaters: TreaterDto[],
  locations: Location[],
  mandates: MandateDto[],
  rapidTestCodeOptions: DropdownOption[],
  pageDataLoaded: boolean,
  locationsMap: IndexSignature,
  mandateMap: IndexSignature,
  cancelTokenSource: CancelTokenSource,
}

class ResourcesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const searchTerm = initialSearchValues;

    this.state = {
      foundResources: [],
      resourcesLoaded: false,
      resource: undefined,
      addOrEditResourceModal: false,
      errorMessage: "",
      resourceGroups: [],
      pageDataLoaded: false,
      treaters: [],
      locations: [],
      mandates: [],
      resourceGroupOptions: [],
      treaterOptions: [],
      locationOptions: [],
      mandateOptions: [],
      rapidTestCodeOptions: [],
      searchTerm,
      cancelTokenSource,
      locationsMap: {},
      mandateMap: {}

    }

    this.fetchPageData();
  }

  fetchPageData = async () => {
    const {cancelTokenSource} = this.state;

    const searchRequest: ResourceGroupSearchDto = {
      includesInactive: true
    }

    Promise.all([
          getAllResourceGroups(searchRequest, cancelTokenSource),
          getAllTreaters(cancelTokenSource),
          getAllMandates(cancelTokenSource),
          getAllLocations(cancelTokenSource),
          getRapidTestCodes(cancelTokenSource)
        ])
        .then(response => {

          const resourceOptions = response[0].map(res => {
            return {
              key: res.id,
              text: res.description,
              value: res.id
            }
          });

          const treaterOptions = response[1].map(tret => {
            return {
              key: tret.id,
              text: tret.name + ", " + tret.firstName,
              value: tret.id
            }
          });

          const mandateOptions = response[2].map(loc => {
            return {
              key: loc.id,
              text: loc.name,
              value: loc.id
            }
          });

          const locationOptions = response[3].map(loc => {
            return {
              key: loc.id,
              text: loc.description,
              value: loc.id
            }
          });


          const mandateMap = _.keyBy(response[2], "id");
          const locationsMap = _.keyBy(response[3], "id");

          const rapidTestCodeOptions = mapToDropdownOptionArray(response[4], "display", "code", "code");

          this.setState({
            resourceGroups: response[0],
            resourceGroupOptions: resourceOptions,
            treaters: response[1],
            treaterOptions: treaterOptions,
            mandates: response[2],
            mandateOptions: mandateOptions,
            mandateMap,
            locations: response[3],
            locationOptions: locationOptions,
            locationsMap,
            rapidTestCodeOptions: rapidTestCodeOptions
          })
        })
        .finally(() => {
          this.setState({
            pageDataLoaded: true
          });
        });
  };

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  openEditModal = (id: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id)
      getResourceById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop)

  }

  showModal = (id?: number, resource?: UpsertResourceDto) => {

    this.setState({
      addOrEditResourceModal: true,
      resource: resource,
      resourceId: id
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addOrEditResourceModal: false,
      resource: undefined,
      resourceId: undefined
    }));
  };

  deleteResource = (id: number) => {
    const {cancelTokenSource} = this.state;

    deleteById(id, cancelTokenSource).then(response => {
          this.setState(({foundResources}: State) => ({
            foundResources: foundResources.filter((resource: ResourceDto) => resource.id != response.id)
          }))
        })
        .catch((error) => this.handleError(error.response.data))
        .finally(this.onChangeResourceForm);
  };

  handleChange = debounce(({values}: ResourcesProps): void => {

    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({resourcesLoaded: false});

    const onFinally = () => {
      this.setState({
        resourcesLoaded: true
      })
    };

    searchResource(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundResources: response,
              searchTerm: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  onChangeResourceForm = () => {
    const {searchTerm, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        resourcesLoaded: true
      })
    };

    searchResource(searchTerm, cancelTokenSource)
        .then(response => {
          this.setState({
            foundResources: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  render(): React.ReactNode {
    const {resourcesLoaded, errorMessage} = this.state;
    return (
        <PraxStyledSegment>

          {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }

          <div>
            {this.renderSearchResourcesSegment()}
          </div>

          <StyledViewTableRow>
            {!resourcesLoaded ? this.renderLoader() : this.renderResourcesSegment()}
          </StyledViewTableRow>

          {this.renderUpsertResourceModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchResourcesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {

            }}
            initialValues={{
              includesInactive: false
            }}
            render={this.renderSearchForm}
        />
    )
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundResources} = this.state;

    const resources = foundResources.length;

    return (
        <PxGrid>
          <GridRow>
            <GridColumn width={3} verticalAlign={'middle'}>
              {getMessage("select")}:
            </GridColumn>
            <GridColumn width={13}>
              <Field
                  name={"searchTerm"}
                  component={Input}
                  fluid
              />
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn width={3} verticalAlign={'middle'}>
            </GridColumn>
            <GridColumn width={4} verticalAlign={'top'}>
              <Field
                  name={"includesInactive"}
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle
              />
            </GridColumn>
            <GridColumn width={9}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </GridColumn>
          </GridRow>
          <SearchResultComponent numResults={resources} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  }

  renderResourcesSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundResources} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundResources.length}
            rowGetter={this.resourceRowGetter}
            rowRenderer={this.foundResourcesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("resources.description")),
                dataKey: "description"
              },
              {
                width: 200,
                label: (getMessage("resources.resourceGroup")),
                dataKey: "resourceGroupId",
                cellRenderer: this.resourceGroupCellRenderer
              },
              {
                width: 500,
                label: (getMessage("resources.locations")),
                dataKey: "locationId",
                cellRenderer: this.locationCellRenderer
              },
              {
                width: 200,
                label: (getMessage("resources.treater")),
                dataKey: "treaterId",
                cellRenderer: this.treaterCellRenderer
              },
              {
                width: 100,
                flexGrow: 1,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  resourceGroupCellRenderer = ({cellData}: any) => {

    const {resourceGroups} = this.state;

    if (cellData) {
      const resourceGroup = resourceGroups.find(rg => rg.id === cellData);

      if (resourceGroup) {
        return resourceGroup.description;
      }

    }
    return "";
  }

  treaterCellRenderer = ({cellData}: any) => {

    const {treaters} = this.state;

    if (cellData) {
      const treater = treaters.find(t => t.id === cellData);

      if (treater) {
        return treater.name + ", " + treater.firstName;
      }

    }
    return "";
  }

  locationCellRenderer = ({rowData}: any) => {

    const {locationsMap} = this.state;
    const locationIds = rowData.locations;
    let locats: string = "";

    if (locationIds && locationIds.length) {
      for (let i = 0; i < locationIds.length; i++) {
        const lId = locationIds[i];
        if (locationsMap[lId]) {
          locats += `${locationsMap[lId].description}`
          if (i < locationIds.length - 1) {
            locats += ", "
          }
        }
      }
    }

    return locats;
  }

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage("resources.active")
    } else {
      return getMessage("resources.inactive")
    }
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("resources.confirmDelete")}
              onConfirm={() => this.deleteResource(cellData)}
          />
        </React.Fragment>
    )
  }

  resourceRowGetter = ({index}: any) => {
    const {foundResources} = this.state;

    Object.assign(foundResources[index], {index: index + 1});

    return foundResources[index]
  };

  foundResourcesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundResources} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openEditModal(foundResources[index].id)}
        >
          {columns}
        </div>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer>
          <Loader inline={'centered'} size={'medium'}>
            {getMessage("resources.loading")}
          </Loader>
        </Dimmer>
    )
  }

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [errorUtils.deletingResourcesNotAllowed];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  renderUpsertResourceModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {
      addOrEditResourceModal,
      resource,
      resourceId,
      resourceGroupOptions,
      treaterOptions,
      locationOptions,
      mandateOptions,
      rapidTestCodeOptions
    } = this.state;

    return (
        <Modal
            open={addOrEditResourceModal}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >

          <Modal.Header>{getMessage("resources.title")}</Modal.Header>

          <FormModalContentContainer>
            <UpsertResourcesForm
                resource={resource}
                resourceId={resourceId}
                onSave={this.onChangeResourceForm}
                onCancel={this.hideModal}
                resourceGroupOptions={resourceGroupOptions}
                treaterOptions={treaterOptions}
                locationOptions={locationOptions}
                mandateOptions={mandateOptions}
                rapidTestCodeOptions={rapidTestCodeOptions}
            >
            </UpsertResourcesForm>
          </FormModalContentContainer>
        </Modal>
    )
  }
}

export default withMessages(ResourcesView);
